import React from "react";
import {
  Stack,
  Box,
  Typography,
  Chip,
  useTheme,
  alpha,
  SxProps,
} from "@mui/material";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { SortConfig } from "../types";
import { usePermissions } from "@/shared/hooks/usePermissions";
import { canReadUsers } from "../../../shared/auth/authorization";
import { useColorMode } from "../../../context/ColorModeContext";

interface SortControlsProps {
  sortConfig: SortConfig | null;
  onSortChange: (key: string) => void;
  containerSx?: SxProps;
}

export const SortControls: React.FC<SortControlsProps> = ({
  sortConfig,
  onSortChange,
  containerSx,
}) => {
  const theme = useTheme();
  const { mode } = useColorMode();
  const permissions = usePermissions();
  const canReadUsersList = canReadUsers(permissions);

  const sortOptions = [
    { key: "details", label: "Details" },
    ...(canReadUsersList ? [{ key: "reportedBy", label: "Reported By" }] : []),
    ...(canReadUsersList ? [{ key: "modifiedBy", label: "Modified By" }] : []),
    { key: "status", label: "Status" },
    { key: "reportedByDate", label: "Reported" },
    { key: "dateModified", label: "Modified" },
  ];

  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{
        overflowX: { xs: "auto", md: "visible" },
        pb: { xs: 1, md: 0 },
        width: "100%",
        "::-webkit-scrollbar": { height: 4 },
        "::-webkit-scrollbar-thumb": {
          backgroundColor: alpha(theme.palette.primary.main, 0.3),
          borderRadius: 2,
        },
        ...containerSx,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          color: "text.secondary",
          mr: 0.5,
        }}
      >
        <SwapVertIcon sx={{ fontSize: "1.25rem" }} />
        <Typography
          variant="body1"
          fontWeight="bold"
          sx={{ whiteSpace: "nowrap" }}
        >
          Sort:
        </Typography>
      </Box>
      {sortOptions.map((option) => {
        const isActive = sortConfig?.key === option.key;
        return (
          <Chip
            key={option.key}
            label={option.label}
            onClick={() => onSortChange(option.key)}
            color={isActive ? "primary" : "default"}
            variant={isActive ? "filled" : "outlined"}
            icon={
              isActive ? (
                sortConfig?.direction === "asc" ? (
                  <ArrowUpwardIcon sx={{ fontSize: "1.125rem !important" }} />
                ) : (
                  <ArrowDownwardIcon sx={{ fontSize: "1.125rem !important" }} />
                )
              ) : undefined
            }
            clickable
            size="medium"
            sx={{
              fontSize: "0.9375rem",
              fontWeight: isActive ? 600 : 400,
              borderRadius: 2,
              transition: "all 0.2s ease",
              ...(!isActive && {
                borderColor: alpha(theme.palette.text.primary, 0.3),
                color: "text.primary",
                ...(mode === "dark" && {
                  bgcolor: alpha(theme.palette.text.primary, 0.06),
                }),
                "&:hover": {
                  bgcolor: alpha(theme.palette.primary.main, 0.12),
                  borderColor: theme.palette.primary.main,
                },
              }),
            }}
          />
        );
      })}
    </Stack>
  );
};
